import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  Avatar,
  AvatarGroup,
  GlobeCheckmarkIcon,
  LockIcon,
  SegmentedIconSwitch,
  UserIcon,
} from "components";
import { Popover } from "components/popover";
import type { SegmentedIconSwitchOption } from "components/switch/SegmentedIconSwitch";
import { memo, useMemo } from "react";
import type { AccessSpecifier } from "shared/types";
import type { ChatMember } from "shared/types/chatMembers";

// ─── Types ───────────────────────────────────────────────────────────

interface ChatModeSwitcherProps {
  activeMode: AccessSpecifier;
  customerUsers: ChatMember[];
  externalUsers: ChatMember[];
  onModeChange: (mode: AccessSpecifier) => void;
  disabled?: boolean;
  disabledModes?: AccessSpecifier[];
  disabledReason?: string;
  isEditorFocused?: boolean;
  onContainerClick?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────

export const ChatModeSwitcher = memo(
  ({
    activeMode,
    customerUsers,
    externalUsers,
    onModeChange,
    disabled,
    disabledModes = [],
    disabledReason,
    isEditorFocused,
    onContainerClick,
  }: ChatModeSwitcherProps) => {
    const theme = useTheme();

    const options: SegmentedIconSwitchOption[] = useMemo(
      () => [
        {
          value: "public",
          label: "Public",
          icon: <GlobeCheckmarkIcon />,
          activeColor: theme.palette.primitive.system.violet[50],
          activeIconColor: theme.palette.primitive.system.violet[800],
          width: 84,
          disabled: disabledModes.includes("public"),
          tooltip: disabledModes.includes("public")
            ? disabledReason
            : undefined,
        },
        {
          value: "private",
          label: "Private",
          icon: <LockIcon />,
          activeColor: theme.palette.primitive.system.blue[50],
          activeIconColor: theme.palette.primitive.system.blue[800],
          width: 84,
          disabled: disabledModes.includes("private"),
          tooltip: disabledModes.includes("private")
            ? disabledReason
            : undefined,
        },
        {
          value: "self",
          label: "Self",
          icon: <UserIcon />,
          activeColor: theme.palette.primitive.neutrals.neutral[25],
          activeIconColor: theme.palette.lightBg.high,
          width: 84,
          disabled: disabledModes.includes("self"),
          tooltip: disabledModes.includes("self") ? disabledReason : undefined,
        },
      ],
      [theme, disabledModes, disabledReason],
    );

    const activeOption = options.find((o) => o.value === activeMode);

    return (
      <Stack
        direction="row"
        onClick={onContainerClick}
        sx={{
          alignItems: "center",
          gap: "16px",
          padding: "8px 16px",
          backgroundColor: activeOption?.activeColor,
        }}
      >
        <SegmentedIconSwitch
          options={options}
          value={activeMode}
          onChange={(val) => onModeChange(val as AccessSpecifier)}
          disabled={disabled}
          aria-label="Chat visibility mode"
          size="small"
          containerSx={{
            backgroundColor: "#0000001A",
            ...(disabled && { opacity: 0.5 }),
          }}
        />
        {/* Avatar group for public mode — show customer + external users */}
        {activeMode === "public" &&
          (customerUsers.length > 0 || externalUsers.length > 0) && (
            <Popover
              sx={{
                cursor: "pointer",
              }}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              paperSx={{ py: "16px", minWidth: "200px" }}
              popoverContent={
                <Stack gap="4px">
                  <Typography
                    variant="body-small"
                    color={theme.palette.lightBg.low}
                    sx={{ mb: "8px", px: "16px" }}
                  >
                    Chat members
                  </Typography>
                  {customerUsers.map((user) => (
                    <Stack
                      key={user.user_id}
                      direction="row"
                      alignItems="center"
                      gap="12px"
                      sx={{
                        py: "8px",
                        px: "16px",
                      }}
                    >
                      <Avatar
                        fullname={user.display_name}
                        variants="sm"
                        src={user.profile_pic || ""}
                      />
                      <Typography variant="body-small">
                        {user.display_name}
                      </Typography>
                    </Stack>
                  ))}
                  {externalUsers.map((user) => (
                    <Stack
                      key={user.user_id}
                      direction="row"
                      alignItems="center"
                      gap="12px"
                      sx={{
                        py: "8px",
                        px: "16px",
                      }}
                    >
                      <Avatar
                        fullname={user.display_name ?? user.email}
                        variants="sm"
                        src={user.profile_pic || ""}
                      />
                      <Typography variant="body-small">
                        {user.display_name ?? user.email}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              }
            >
              <Box
                sx={{
                  opacity: isEditorFocused ? 1 : 0,
                }}
              >
                <AvatarGroup
                  max={8}
                  childWidthHeight="24px"
                  overlapMargin="-8px"
                  stackOrder="last"
                >
                  {customerUsers.map((user) => (
                    <Avatar
                      showBorder
                      key={user.user_id}
                      fullname={user.display_name}
                      src={user.profile_pic || ""}
                      tooltip
                    />
                  ))}
                  {externalUsers.map((user) => (
                    <Avatar
                      showBorder
                      key={user.user_id}
                      fullname={user.email}
                      src={user.profile_pic || ""}
                      tooltip
                    />
                  ))}
                </AvatarGroup>
              </Box>
            </Popover>
          )}
      </Stack>
    );
  },
);
